import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";

const ReportsDashboard = () => {
  const [banks, setBanks] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [netWorthRows, setNetWorthRows] = useState([]);
  const [monthlyPassiveIncome, setMonthlyPassiveIncome] = useState(0);
  const [spendingTrends, setSpendingTrends] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const [banksRes, periodsRes, netWorthRes, passiveIncomeRes, spendingRes, currenciesRes] = await Promise.all([
          apiClient.get("/banks"),
          apiClient.get("/periods"),
          apiClient.get("/reports/net-worth"),
          apiClient.get("/reports/passive-income"),
          apiClient.get("/reports/spending-trends"),
          apiClient.get("/currencies"),
        ]);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setBanks(Array.isArray(banksRes.data) ? banksRes.data : []);
        setPeriods(Array.isArray(periodsRes.data) ? periodsRes.data : []);
        setNetWorthRows(Array.isArray(netWorthRes.data) ? netWorthRes.data : []);
        setMonthlyPassiveIncome(Number(passiveIncomeRes.data?.monthlyIncome || 0));
        setSpendingTrends(Array.isArray(spendingRes.data?.Categories) ? spendingRes.data.Categories : []);
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load dashboard report.");
        setBanks([]);
        setPeriods([]);
        setNetWorthRows([]);
        setMonthlyPassiveIncome(0);
        setSpendingTrends([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const summary = useMemo(() => {
    const totalsByCurrency = (filterBank) => Object.entries(
      banks.reduce((totals, bank) => {
        if (bank.LatestBalanceAmount == null) return totals;
        if (!filterBank(bank)) return totals;
        const currency = bank.CurrencyDescription || "Unspecified";
        totals[currency] = (totals[currency] || 0) + Number(bank.LatestBalanceAmount || 0);
        return totals;
      }, {})
    )
      .map(([currency, total]) => ({ currency, total }))
      .sort((left, right) => left.currency.localeCompare(right.currency));
    const cashAvailableByCurrency = totalsByCurrency((bank) => !Number(bank.IsRetirement));
    const retirementFundsByCurrency = totalsByCurrency((bank) => Number(bank.IsRetirement));
    const usablePeriods = periods.filter((period) => {
      const income = Number(period.IncomeAmount || 0);
      const expenses = Number(period.ExpensesAmount || 0);
      const savings = Number(period.SavingsAmount || 0);
      return income > 0 || expenses > 0 || savings > 0;
    });
    const sortedUsablePeriods = [...usablePeriods].sort(
      (a, b) => String(a.DateEnd || "").localeCompare(String(b.DateEnd || "")) || Number(a.PeriodId || 0) - Number(b.PeriodId || 0)
    );
    const averagePeriods = sortedUsablePeriods.length >= 12 ? sortedUsablePeriods.slice(-12) : sortedUsablePeriods;
    const periodCount = averagePeriods.length || 1;
    const sortedNetWorthRows = [...netWorthRows]
      .sort((a, b) => String(a.DateEnd || "").localeCompare(String(b.DateEnd || "")) || Number(a.PeriodId || 0) - Number(b.PeriodId || 0))
    const latestNetWorth = [...sortedNetWorthRows]
      .reverse()
      .find((row) => Number(row.NetWorthAmount || 0) > 0) || sortedNetWorthRows.at(-1);
    const latestLiabilitiesPeriod = [...periods]
      .sort((a, b) => String(a.DateEnd || "").localeCompare(String(b.DateEnd || "")) || Number(a.PeriodId || 0) - Number(b.PeriodId || 0))
      .reverse()
      .find((period) => Number(period.LiabilitiesAmount || 0) > 0) || sortedUsablePeriods.at(-1) || periods.at(-1);

    const averageMonthlyIncome = averagePeriods.reduce((sum, period) => sum + Number(period.IncomeAmount || 0), 0) / periodCount;
    const averageMonthlyExpenditure = averagePeriods.reduce(
      (sum, period) => sum + Math.max(0, Number(period.ExpensesAmount || 0) - Number(period.SavingsAmount || 0)),
      0
    ) / periodCount;
    const savingsRatePeriods = averagePeriods.filter((period) => Number(period.IncomeAmount || 0) > 0);
    const averageSavingsRate = savingsRatePeriods.length === 0
      ? 0
      : savingsRatePeriods.reduce(
          (sum, period) => sum + (Number(period.SavingsAmount || 0) / Number(period.IncomeAmount || 0)),
          0
        ) / savingsRatePeriods.length;

    const transactionMonthsWithData = Array.from(
      spendingTrends.reduce((acc, category) => {
        const points = Array.isArray(category.Points) ? category.Points : [];
        points.forEach((point) => {
          if (Number(point.Expenditure || 0) > 0) {
            acc.set(Number(point.PeriodId), {
              PeriodId: Number(point.PeriodId),
              Description: point.Description || `Period ${point.PeriodId}`,
              DateEnd: point.DateEnd || "",
            });
          }
        });
        return acc;
      }, new Map()).values()
    )
      .sort((a, b) => String(a.DateEnd || "").localeCompare(String(b.DateEnd || "")) || Number(a.PeriodId || 0) - Number(b.PeriodId || 0));

    const categoryAverageMonths =
      transactionMonthsWithData.length > 12
        ? transactionMonthsWithData.slice(-12)
        : transactionMonthsWithData;
    const categoryAverageMonthIds = new Set(categoryAverageMonths.map((period) => Number(period.PeriodId)));
    const categoryAverageMonthCount = categoryAverageMonths.length;

    const averageSpendByCategory = spendingTrends
      .map((category) => {
        const points = Array.isArray(category.Points) ? category.Points : [];
        const relevantPoints = points.filter((point) => categoryAverageMonthIds.has(Number(point.PeriodId)));
        const total = relevantPoints.reduce((sum, point) => sum + Number(point.Expenditure || 0), 0);
        return {
          Category: category.Category,
          AverageSpend: categoryAverageMonthCount > 0 ? total / categoryAverageMonthCount : 0,
        };
      })
      .filter((item) => item.AverageSpend > 0)
      .sort((left, right) => String(left.Category || "").localeCompare(String(right.Category || "")));

    return {
      cashAvailableByCurrency,
      retirementFundsByCurrency,
      latestNetWorth: Number(latestNetWorth?.NetWorthAmount || 0),
      latestNetWorthPeriod: latestNetWorth?.Description || "-",
      latestLiabilities: Number(latestLiabilitiesPeriod?.LiabilitiesAmount || 0),
      latestLiabilitiesPeriod: latestLiabilitiesPeriod?.Description || "-",
      averagePeriodCount: periodCount,
      categoryAverageMonthCount,
      averageSavingsRate,
      averageMonthlyIncome,
      averageMonthlyExpenditure,
      averageSpendByCategory,
    };
  }, [banks, periods, netWorthRows, spendingTrends]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Dashboard
          </p>
          <p className="report-context-note">
            Averages use the last {summary?.averagePeriodCount || 0} months when available; otherwise they use all available records.
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading dashboard report...</p>}

      {!loading && (
        <section className="resource-list">
          <div className="dashboard-grid report-summary-strip">
            <div className="metric-card metric-card-compact dashboard-primary-metric">
              <strong>Net Worth</strong>
              <div className="dashboard-primary-metric-value">{formatMoney(summary.latestNetWorth, defaultCurrency || "Default")}</div>
              <div className="dashboard-primary-metric-meta">{summary.latestNetWorthPeriod}</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Cash Available</strong>
              {summary.cashAvailableByCurrency.length === 0 ? (
                <div className="resource-meta">No non-retirement bank balances recorded</div>
              ) : (
                summary.cashAvailableByCurrency.map((item) => (
                  <div key={item.currency} className="resource-meta">
                    {item.currency}: {formatMoney(item.total, item.currency)}
                  </div>
                ))
              )}
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Retirement Funds Available</strong>
              {summary.retirementFundsByCurrency.length === 0 ? (
                <div className="resource-meta">No retirement bank balances recorded</div>
              ) : (
                summary.retirementFundsByCurrency.map((item) => (
                  <div key={item.currency} className="resource-meta">
                    {item.currency}: {formatMoney(item.total, item.currency)}
                  </div>
                ))
              )}
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Average Savings Rate</strong>
              <div className="dashboard-savings-rate-value">{(summary.averageSavingsRate * 100).toFixed(1)}%</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Average Monthly Income</strong>
              <div className="resource-meta">{formatMoney(summary.averageMonthlyIncome, defaultCurrency || "Default")}</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Monthly Passive Income</strong>
              <div className="resource-meta">{formatMoney(monthlyPassiveIncome, defaultCurrency || "Default")}</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Average Monthly Expenditure</strong>
              <div className="resource-meta">{formatMoney(summary.averageMonthlyExpenditure, defaultCurrency || "Default")}</div>
              <div className="resource-meta">Excluding savings</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Liabilities</strong>
              <div className="resource-meta">{formatMoney(summary.latestLiabilities, defaultCurrency || "Default")}</div>
              <div className="resource-meta">{summary.latestLiabilitiesPeriod}</div>
            </div>
          </div>

          <section className="panel">
            <div className="section-heading">
              <div>
                <h2>Average Spend By Category</h2>
              </div>
            </div>
            {summary.averageSpendByCategory.length === 0 ? (
              <p className="notice notice-muted">No category spending data found.</p>
            ) : (
              <div className="dashboard-grid">
                {summary.averageSpendByCategory.map((item) => (
                  <div key={item.Category} className="metric-card metric-card-compact">
                    <strong>{item.Category}</strong>
                    <div className="resource-meta">{formatMoney(item.AverageSpend, defaultCurrency || "Default")}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </section>
      )}
    </div>
  );
};

export default ReportsDashboard;
